import { z } from 'zod';

import { rangeComparisonSchema } from './talent-discovery-types-v2';

// --------------------
// Common primitives
// --------------------
const promptItemSchema = z.object({
  name: z.string(),
  relevantPromptText: z.string(),
});

/**
 * A place, with the broader place that disambiguates it as the model knows it
 * ("Portland" in "Maine, United States"), or null when the name is
 * unambiguous. The geography resolver composes its query from the two fields.
 */
const geographyItemSchema = promptItemSchema.extend({
  name: z
    .string()
    .describe("The place alone, in the user's wording, without its qualifier."),
  containedIn: z
    .union([z.string(), z.null()])
    .describe(
      'A real broader place name that disambiguates this place, or JSON null when unqualified. Never a string null/none, empty string, or punctuation.'
    ),
});

const rangeItemSchema = z.object({
  start_value: z.union([z.number(), z.null()]).nullable(),
  end_value: z.union([z.number(), z.null()]).nullable(),
  relevantPromptText: z.string(),
  // Required, with no tolerant twin: the screener validates the response
  // against this same schema, and a reused object cannot carry an optional
  // field into OpenAI's strict schema (see RANGE_COMPARISONS).
  comparison: rangeComparisonSchema,
});

// --------------------
// Predefined enums (same as TalentDiscovery where applicable)
// --------------------
const GenderEnum = z.enum(['Male', 'Female']);

const EthnicityEnum = z.enum([
  'White',
  'Asian',
  'Hispanic',
  'Black',
  'Native',
  'Multiple',
]);

const HighestDegreeEnum = z.enum([
  'High School',
  'Associate',
  'Bachelor',
  'MBA',
  'Master',
  'Doctor',
]);

const SeniorityEnum = z.enum([
  'Entry Level',
  'Junior',
  'Associate',
  'Manager',
  'Director',
  'Executive',
  'Senior Executive',
]);

// Funding stage predefined list
const FundingStageEnum = z.enum([
  'Angel/Pre-Seed',
  'Seed',
  'Series A',
  'Series B',
  'Series C',
  'Series D',
  'Series E+',
  'IPO',
]);

// Typed prompt items for predefined values
const genderItemSchema = promptItemSchema.extend({ name: GenderEnum });
const ethnicityItemSchema = promptItemSchema.extend({
  name: EthnicityEnum,
});
const highestDegreeItemSchema = promptItemSchema.extend({
  name: HighestDegreeEnum,
});
const seniorityItemSchema = promptItemSchema.extend({
  name: SeniorityEnum,
});
const fundingStageItemSchema = promptItemSchema.extend({
  name: FundingStageEnum,
});

// --------------------
// Segment filter (people-side filters allowed inside segment.filter)
// --------------------
const segmentPeopleFilterSchema = z.object({
  geography: z.array(geographyItemSchema).nullable(),
  industry: z.array(promptItemSchema).nullable(),
  role: z.array(promptItemSchema).nullable(),

  seniority: z.array(seniorityItemSchema).nullable(),
  highest_degree: z.array(highestDegreeItemSchema).nullable(),
  gender: z.array(genderItemSchema).nullable(),
  ethnicity: z.array(ethnicityItemSchema).nullable(),
});

// One segment entry for a metric (headcount/inflow/outflow/etc.)
const segmentItemSchema = z.object({
  start_value: z.union([z.number(), z.null()]).nullable(),
  end_value: z.union([z.number(), z.null()]).nullable(),
  relevantPromptText: z.string(),
  filter: segmentPeopleFilterSchema,
});

// All supported metric keys
const segmentsSchema = z.object({
  headcount: z.array(segmentItemSchema).nullable(),
  inflow: z.array(segmentItemSchema).nullable(),
  outflow: z.array(segmentItemSchema).nullable(),
  hiring: z.array(segmentItemSchema).nullable(),
  attrition: z.array(segmentItemSchema).nullable(),
  growth: z.array(segmentItemSchema).nullable(),
  growth_yoy: z.array(segmentItemSchema).nullable(),
  salary: z.array(segmentItemSchema).nullable(),
  tenure: z.array(segmentItemSchema).nullable(),
});

// --------------------
// Primary selector (ONLY industry/geography/role)
// --------------------
const primarySelectorSchema = z.object({
  industry: z.array(promptItemSchema).nullable(),
  geography: z.array(geographyItemSchema).nullable(),
  role: z.array(promptItemSchema).nullable(),
});

// --------------------
// Company info selectors (company discovery filters)
// --------------------
const companyInfoSelectorsSchema = z.object({
  competitors_of: z.array(promptItemSchema).nullable(), // postprocess: company -> rcid int[]
  is_public: z.boolean().nullable(),
  is_subsidiary: z.boolean().nullable(),

  hq_geography: z.array(geographyItemSchema).nullable(), // postprocess: geo -> int[] (or split to hq_country/hq_region/hq_msa later)

  funding_stage: z.array(fundingStageItemSchema).nullable(), // predefined list
  year_founded: z.array(rangeItemSchema).nullable(),
  valuation: z.array(rangeItemSchema).nullable(),
  total_funding_amount: z.array(rangeItemSchema).nullable(),
  last_funding_amount: z.array(rangeItemSchema).nullable(),
  last_funding_year: z.array(rangeItemSchema).nullable(),

  keywords: z.array(z.string()).nullable(),

  founder_prev_company: z.array(promptItemSchema).nullable(), // postprocess: company -> rcid int[]
  founder_prev_university: z.array(promptItemSchema).nullable(), // postprocess: university -> int[]

  founder_gender: z.array(genderItemSchema).nullable(),
  founder_ethnicity: z.array(ethnicityItemSchema).nullable(),
});

const entityInfoSelectorsSchema = z.object({
  company_info_selectors: companyInfoSelectorsSchema,
});

// --------------------
// Unknown filters schema (LLM must only put truly unsupported items here)
// --------------------
const unknownFilterItemSchema = z.object({
  name: z.string(),
  relevantPromptText: z.string(),
  explanation: z.string(),
});

// --------------------
// Final LLM output schema (what the model must return)
// --------------------
const companyDiscoveryAiOutputSchema = z.object({
  filters: z.object({
    primary_selector: primarySelectorSchema,
    entity_info_selectors: entityInfoSelectorsSchema,
    segments: segmentsSchema,
  }),
  unknownFilters: z.array(unknownFilterItemSchema),
});

// Useful inferred types
export type CompanyDiscoveryAiOutput = z.infer<
  typeof companyDiscoveryAiOutputSchema
>;

// --------------------
// For OpenAI (strict output schema)
// Allow raw text extraction for funding_stage and is_subsidiary
// --------------------
// Funding_stage schema that allows any string (for extraction only)
const fundingStageItemSchemaForOpenAI = promptItemSchema.extend({
  name: z.string(),
});

const companyInfoSelectorsForOpenAI =
  companyDiscoveryAiOutputSchema.shape.filters.shape.entity_info_selectors.shape.company_info_selectors.extend(
    {
      funding_stage: z.array(fundingStageItemSchemaForOpenAI).nullable(),
      // Allow either boolean or descriptive text for extraction
      is_subsidiary: z.union([z.boolean(), z.string()]).nullable(),
      is_public: z.union([z.boolean(), z.string()]).nullable(),
    }
  );

const entityInfoSelectorsForOpenAI =
  companyDiscoveryAiOutputSchema.shape.filters.shape.entity_info_selectors.extend(
    {
      company_info_selectors: companyInfoSelectorsForOpenAI,
    }
  );

const companyDiscoveryAiFiltersForOpenAI =
  companyDiscoveryAiOutputSchema.shape.filters.extend({
    primary_selector: primarySelectorSchema.extend({
      geography: z
        .null()
        .describe(
          'Always null. Company or bare locations belong to company_info_selectors.hq_geography; workforce locations belong to the corresponding segment.filter.geography.'
        ),
    }),
    entity_info_selectors: entityInfoSelectorsForOpenAI,
  });

// Create final OpenAI schema
export const companyDiscoveryAiOutputSchemaForOpenAI = z.object({
  filters: companyDiscoveryAiFiltersForOpenAI,
  unknownFilters: z.array(unknownFilterItemSchema),
});
